/**
 * Empty states for the teacher open-shifts board (BON-242).
 *
 * Four of them, because the fix differs each time — and one is the launch-day
 * default: a teacher who has never opened the availability form. Telling that
 * person their days are "marked off" sends them to a blank grid with nothing
 * to undo, so it gets its own sentence and its own CTA.
 *
 * Split out of TeacherOpenShifts to keep that component under the lint cap.
 */
import { Link } from 'react-router-dom';

import type { EligibilityGap, FilteredReasons } from './teacherApi';

export function Empty({
  title,
  body,
  cta,
}: {
  title: string;
  body: string;
  cta?: { href: string; label: string };
}) {
  return (
    <div style={{ display: 'grid', gap: 8, maxWidth: 440 }}>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>{body}</p>
      {cta && (
        // Link, not <a href>: a raw anchor reloads the document, dropping the
        // React Query cache and re-running the Auth0 bootstrap.
        <Link to={cta.href} className="nb-btn" style={{ justifySelf: 'start' }}>
          {cta.label}
        </Link>
      )}
    </div>
  );
}

export function EmptyState({ filtered, gap }: { filtered: FilteredReasons; gap: EligibilityGap }) {
  const { noAvailabilitySet, outsideDeclaredHours, alreadyWorking } = filtered;
  const plural = (n: number) => (n === 1 ? '' : 's');

  // Account setup, not shift supply. These two must come first and must never
  // read as "nothing published" — the teacher can't fix either one themselves,
  // and telling them to wait for their director is a dead end.
  if (gap === 'no_positions') {
    return (
      <Empty
        title="No positions on your account yet"
        body="Open shifts are matched to the positions you’re cleared to work, and none are set for you. Ask your regional director to add them — teacher, assistant, or teacher-in-training — and shifts will appear here."
      />
    );
  }
  if (gap === 'no_regions') {
    return (
      <Empty
        title="No region on your account yet"
        body="Shifts are published per region, and your account isn’t assigned to one. Ask your regional director to add you and this board will fill in."
      />
    );
  }

  if (noAvailabilitySet > 0) {
    return (
      <Empty
        title="Set your availability to see shifts"
        body={`There ${noAvailabilitySet === 1 ? 'is' : 'are'} ${noAvailabilitySet} open shift${plural(noAvailabilitySet)} in your region, but we don’t know when you can work yet. Tell us your weekly rhythm and they’ll show up here.`}
        cta={{ href: '/me/availability', label: 'Set my availability' }}
      />
    );
  }
  if (outsideDeclaredHours > 0) {
    return (
      <Empty
        title="Nothing fits your availability"
        body={`There ${outsideDeclaredHours === 1 ? 'is' : 'are'} ${outsideDeclaredHours} open shift${plural(outsideDeclaredHours)} in your region, but they fall outside the days and hours you’ve said you can work.`}
        cta={{ href: '/me/availability', label: 'Review my availability' }}
      />
    );
  }
  if (alreadyWorking > 0) {
    return (
      <Empty
        title="You’re already booked those days"
        body={`The ${alreadyWorking === 1 ? 'only open shift' : `${alreadyWorking} open shifts`} in your region ${alreadyWorking === 1 ? 'falls' : 'fall'} on days you’re already crewed on.`}
      />
    );
  }
  return (
    <Empty
      title="No open shifts right now"
      body="Nothing’s been published for your region yet. New shifts show up here the moment your director posts them."
    />
  );
}
